import React, { useState, useEffect, useCallback } from "react";
import { formatDateTime } from "../utils/dateUtils";
import {
  getAllUsers,
  enableUser,
  disableUser,
  setAdminStatus,
  deleteUser
} from "../services/adminService";

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [roleFilter, setRoleFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllUsers({
        page,
        pageSize,
        role: roleFilter || undefined,
        search: searchTerm || undefined
      });
      setUsers(response.users || []);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Failed to load users:", error);
      alert("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, roleFilter, searchTerm]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleEnableUser = async (userId) => {
    try {
      await enableUser(userId);
      loadUsers();
    } catch (error) {
      console.error("Failed to enable user:", error);
      alert("Failed to enable user");
    }
  };

  const handleDisableUser = async (userId) => {
    if (!window.confirm("Are you sure you want to disable this user?")) return;
    try {
      await disableUser(userId);
      loadUsers();
    } catch (error) {
      console.error("Failed to disable user:", error);
      alert("Failed to disable user");
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    if (!window.confirm(`${currentStatus ? "Remove" : "Grant"} admin privileges?`)) return;
    try {
      await setAdminStatus(userId, !currentStatus);
      loadUsers();
    } catch (error) {
      console.error("Failed to update admin status:", error);
      alert("Failed to update admin status");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This will disable their account.")) return;
    try {
      await deleteUser(userId);
      loadUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert("Failed to delete user");
    }
  };

  return (
    <div className="flex-1 p-6 overflow-auto bg-gray-100 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">User Management</h1>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded shadow mb-6 p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="w-48">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Roles</option>
                <option value="dealer">Dealer</option>
                <option value="client">Client</option>
              </select>
            </div>
            <button
              onClick={() => {
                setSearchTerm("");
                setRoleFilter("");
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center">
            <div className="text-gray-600 dark:text-gray-400">Loading users...</div>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 rounded shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.auth0Picture && (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user.auth0Picture}
                              alt=""
                            />
                          )}
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                              {user.name || user.auth0Name}
                              {user.isAdmin && (
                                <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 text-xs font-bold rounded">
                                  ADMIN
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{user.email || user.auth0Email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200">
                          {user.role || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.isEnabled
                              ? "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200"
                              : "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"
                          }`}
                        >
                          {user.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.lastLogin ? formatDateTime(user.lastLogin) : "Never"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          {user.isEnabled ? (
                            <button
                              onClick={() => handleDisableUser(user.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                            >
                              Disable
                            </button>
                          ) : (
                            <button
                              onClick={() => handleEnableUser(user.id)}
                              className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                            >
                              Enable
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleAdmin(user.id, user.isAdmin)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {user.isAdmin ? "Remove Admin" : "Make Admin"}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && (
              <div className="bg-white rounded shadow mt-4 px-6 py-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {pagination.page + 1} of {pagination.totalPages} ({pagination.totalItems} total users)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed text-gray-700 rounded transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.totalPages}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed text-gray-700 rounded transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
